import React from 'react';
import { Shield, Lock, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SEO } from './SEO';

export const PrivacyPolicy = () => {
  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '80px'
    }}>
      <SEO 
        title="Privacy Policy" 
        description="Toolmetrico Privacy Policy. Learn how our 100% Client-Side Processing architecture guarantees your security, data privacy, and GDPR/CCPA compliance."
        keywords="privacy policy, data security, toolmetrico privacy, client-side processing, secure digital tools, gdpr compliant tools"
      />
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '32px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Privacy Policy</span>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(5, 117, 251, 0.1)',
          color: 'var(--primary)',
          width: '64px',
          height: '64px',
          borderRadius: '20px',
          marginBottom: '20px'
        }}>
          <Shield size={32} />
        </div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', letterSpacing: '-0.02em' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
          Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      <div className="legal-content-container">
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={20} style={{ color: 'var(--primary)' }} /> 1. Introduction
          </h2>
          <p>
            Welcome to Toolmetrico ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy outlines our practices regarding the collection, use, and disclosure of information when you use our web application and services (collectively, the "Services").
          </p>
          <p>
            By accessing or using our Services, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Lock size={20} style={{ color: 'var(--primary)' }} /> 2. Client-Side Processing Guarantee
          </h2>
          <p>
            We prioritize your data security by utilizing a <strong>100% Client-Side Processing</strong> architecture for our primary tools. This means:
          </p>
          <ul style={{
            fontSize: '0.95rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            listStyleType: 'none',
            padding: 0,
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            textAlign: 'left'
          }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div><strong>No Uploads:</strong> When you use tools such as the Image Compressor, Background Remover, or PDF Utilities, your files are processed directly within your web browser. We do not upload your images, documents, or data to our servers.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div><strong>Local Encryption:</strong> Passwords, cryptographic hashes, and generated keys utilize your hardware's local <code>crypto.getRandomValues</code> API. They never leave your machine.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div><strong>Offline Encoding:</strong> QR codes and barcodes are encoded locally. No payload text is transmitted to remote APIs.</div>
            </li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            3. Information We Collect
          </h2>
          <p>
            Because our tools operate primarily client-side, we collect very limited information:
          </p>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '16px 0 8px 0' }}>a. Automatically Collected Information</h3>
          <p>
            When you visit, use, or navigate the Services, we may automatically collect certain information. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, and information about how and when you use our Services. This information is primarily needed to maintain the security and operation of our Services, and for our internal analytics and reporting purposes.
          </p>
          
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '16px 0 8px 0' }}>b. Account Information</h3>
          <p>
            If you choose to register an account with us, we collect personal information that you voluntarily provide, which may include your email address and authentication credentials.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            4. How We Use Your Information
          </h2>
          <p>
            We use the information we collect or receive for various business purposes, including:
          </p>
          <ul style={{
            fontSize: '0.95rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            listStyleType: 'none',
            padding: 0,
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            textAlign: 'left'
          }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>To facilitate account creation and logon processes.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>To protect our Services (e.g., fraud monitoring and prevention).</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>To respond to user inquiries and offer support to users.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>To improve our website functionality, analytics, and user experience.</div>
            </li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            5. Cookies and Tracking Technologies
          </h2>
          <p>
            We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Notice.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            6. Data Security
          </h2>
          <p>
            We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            7. Your Privacy Rights (GDPR & CCPA)
          </h2>
          <p>
            Depending on your geographic location, you may have certain rights under applicable data protection laws, including the right to:
          </p>
          <ul style={{
            fontSize: '0.95rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            listStyleType: 'none',
            padding: 0,
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            textAlign: 'left'
          }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>Request access and obtain a copy of your personal information.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>Request rectification or erasure of your personal information.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>Restrict the processing of your personal information.</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>If applicable, to data portability.</div>
            </li>
          </ul>
          <p>
            To make such a request, please contact us using the details provided below. We will consider and act upon any request in accordance with applicable data protection laws.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            8. Changes to This Policy
          </h2>
          <p>
            We may update this privacy notice from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification.
          </p>
        </section>

        <section>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '16px' }}>
            9. Contact Us
          </h2>
          <p>
            If you have questions or comments about this notice, you may email us at <strong>privacy@toolmetrico.com</strong> or by post to our registered office.
          </p>
        </section>
      </div>

      <style>{`
        .breadcrumb-back:hover {
          color: var(--primary) !important;
          transform: translateX(-2px);
        }

        .legal-content-container {
          background: #ffffff;
          border: 1px solid var(--border-color);
          border-radius: 24px;
          padding: 40px;
          box-shadow: var(--shadow-sm);
          color: var(--text-primary);
          line-height: 1.8;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .legal-content-container {
            padding: 24px 20px;
            border-radius: 16px;
          }
        }
      `}</style>
    </div>
  );
};
